import { useState, useEffect, useRef, useCallback } from "react";
import { api, wsUrl } from "./api";

// Shared realtime room hook. Fetches a full snapshot and refetches on any WS event.
export function useRoom(code, { role = "console", teamId = null, pin = null } = {}) {
  const [snap, setSnap] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const headers = pin ? { "X-Console-Pin": pin } : {};

  const refetch = useCallback(async () => {
    if (!code) return null;
    try {
      const { data } = await api.get(`/session/${code}`, { headers: pin ? { "X-Console-Pin": pin } : {} });
      setSnap(data);
      return data;
    } catch (e) {
      return null;
    }
  }, [code, pin]);

  useEffect(() => {
    if (!code) return;
    let stopped = false;
    refetch();

    const connect = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(wsUrl(code, role, teamId));
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onclose = () => {
          setConnected(false);
          if (!stopped) reconnectRef.current = setTimeout(connect, 2000);
        };
        ws.onerror = () => { try { ws.close(); } catch (e) {} };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            setLastEvent({ ...msg, _ts: Date.now() });
          } catch (e) {}
          refetch();
        };
      } catch (e) {
        reconnectRef.current = setTimeout(connect, 2000);
      }
    };
    connect();

    return () => {
      stopped = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { wsRef.current && wsRef.current.close(); } catch (e) {}
    };
  }, [code, role, teamId, refetch]);

  return { snap, connected, refetch, lastEvent };
}

// Server-authoritative countdown. Renders remaining seconds from ends_at.
export function useCountdown(timer) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!timer || !timer.ends_at || !timer.running) { setRemaining(0); return; }
    const end = new Date(timer.ends_at).getTime();
    const tick = () => setRemaining(Math.max(0, Math.round((end - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [timer && timer.ends_at, timer && timer.running]);
  return remaining;
}
